import { Alert, Descriptions, Skeleton, Space, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime, formatDuration, utcTooltip } from '@/shared/lib/format'
import { NasHealthTag } from './NasHealthTag'
import { formatWindow, useNasHealthReason } from './nasHealthText'
import type { NasHealth } from './nasApi'

interface NasHealthDetailsProps {
  /** `undefined` — belgi hali kelmagan yoki so'rov xato bergan */
  health: NasHealth | undefined
  windowSeconds: number | null
  loading: boolean
  /** So'rov xato berdimi — bo'lsa sabab AYTILADI, jimgina yashirilmaydi */
  failed: boolean
}

/**
 * Kartochkadagi "Holat" bo'limi.
 *
 * <h2>⚠️ Har raqam qayerdan kelgani AYTILADI</h2>
 * Bu sahifa infratuzilma darajasida xavfli (`NasPage` sarlavhasi) va
 * bu yerdagi raqamlarga qarab "routerga borish kerakmi" degan qaror
 * qabul qilinadi. Manbasi tushunarsiz raqam — eng yomon holatda noto'g'ri
 * qarorga, eng yaxshi holatda ishonchsizlikka olib keladi. Shuning uchun:
 *
 *   · "Oxirgi ulanish" — bu auth URINISHI emas, MUVAFFAQIYATLI ulanish
 *     (sabab tooltip'da: `radpostauth` da qurilma manzili yo'q)
 *   · "Qayta yuklanishlar" — DOIM oyna uzunligi bilan birga: "0 ta"
 *     aslida "oxirgi 7 kunda 0 ta" degani
 *   · "Interim oralig'i" — qurilmaning O'ZI aytgan qiymat, chegara
 *     shundan hisoblanadi
 */
export function NasHealthDetails({ health, windowSeconds, loading, failed }: NasHealthDetailsProps) {
  const { t } = useTranslation()
  const reason = useNasHealthReason()

  return (
    <>
      <Typography.Title level={5}>{t('nas.health.title')}</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('nas.health.sourceNote')}
      </Typography.Paragraph>

      {failed ? (
        // ⚠️ Xato YUTILMAYDI. Belgi ko'rinmasa operator uni "hammasi
        // joyida" deb o'qishi mumkin — shuning uchun nima ishlamagani
        // va nima ishlayotgani ochiq yoziladi.
        <Alert type="warning" showIcon message={t('nas.health.unavailable')} />
      ) : loading || !health ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('nas.health.column')}>
              <Space direction="vertical" size={2}>
                <NasHealthTag health={health} windowSeconds={windowSeconds} loading={false} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {reason(health, windowSeconds)}
                </Typography.Text>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t('nas.health.lastContact')}>
              {health.lastContact ? (
                <Tooltip title={utcTooltip(health.lastContact)}>
                  <span>
                    {formatDateTime(health.lastContact)}
                    {health.silentSeconds === null ? null : (
                      <Typography.Text type="secondary">
                        {' · '}
                        {t('nas.health.ago', { duration: formatDuration(health.silentSeconds) })}
                      </Typography.Text>
                    )}
                  </span>
                </Tooltip>
              ) : (
                t('common.dash')
              )}
            </Descriptions.Item>

            <Descriptions.Item label={t('nas.health.activeSessions')}>
              {health.state === 'UNMATCHED' ? t('common.dash') : health.activeSessions}
            </Descriptions.Item>

            <Descriptions.Item label={t('nas.health.lastSessionStart')}>
              <Space direction="vertical" size={2}>
                {health.lastSessionStart ? (
                  <Tooltip title={utcTooltip(health.lastSessionStart)}>
                    <span>{formatDateTime(health.lastSessionStart)}</span>
                  </Tooltip>
                ) : (
                  <span>{t('common.dash')}</span>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('nas.health.lastSessionStartHint')}
                </Typography.Text>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t('nas.health.reboots')}>
              <Space direction="vertical" size={2}>
                {/* ⚠️ Son OYNA bilan birga: "0 ta" o'z-o'zicha "hech qachon
                    qayta yuklanmagan" degan yolg'on xulosaga olib kelardi */}
                <span>
                  {t('nas.health.rebootsValue', {
                    count: health.reboots,
                    window: formatWindow(windowSeconds),
                  })}
                </span>
                {health.lastReboot ? (
                  <Tooltip title={utcTooltip(health.lastReboot)}>
                    <Typography.Text type={health.reboots > 0 ? 'warning' : 'secondary'}>
                      {t('nas.health.lastReboot')}: {formatDateTime(health.lastReboot)}
                    </Typography.Text>
                  </Tooltip>
                ) : null}
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label={t('nas.health.interim')}>
              <Space direction="vertical" size={2}>
                <span>
                  {health.interimInterval === null
                    ? t('nas.health.interimUnknown')
                    : formatDuration(health.interimInterval)}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('nas.health.thresholdValue', {
                    threshold: formatDuration(health.thresholdSeconds),
                  })}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('nas.health.interimHint')}
                </Typography.Text>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </>
  )
}
